import noDataImg from "../../../assets/img/no-data.png"
import { CompactTable } from "@table-library/react-table-library/compact"
import classNames from "classnames"
import { SelectPicker } from "rsuite"
import React, { useEffect, useState } from "react"
import useTranslate from "../../../hooks/useTranslate"
import { formatDate } from "../../../utils/datetime"
import {
  barChartConfig,
  GroupingType,
  lineChartConfig,
  lineChartConfigWithThreshold,
  numberComparison,
  thReachedColor, thTopColor
} from "./PatientGameStatsCommon"
import { usePagination } from "@table-library/react-table-library/pagination"
import { useTheme } from "@table-library/react-table-library/theme"
import { getTheme } from "@table-library/react-table-library/mantine"
import { FruitReactionPlayedGameResultDto } from "../../../sdk"
import { Chart } from "chart.js/auto"
import dayjs from "dayjs"

type PlayedGameData = FruitReactionPlayedGameResultDto & { id: number }

interface FruitReactionGameStatsProps {
  fullData: PlayedGameData[]
  filteredData: PlayedGameData[]
}

export function FruitReactionGameStats({fullData, filteredData}: FruitReactionGameStatsProps) {
  const translateLocalazy = useTranslate()
  const theme = useTheme(getTheme())
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [grouping, setGrouping] = useState<GroupingType | null>(null)

  useEffect(() => {
    const charts: Chart[] = []

    let playedGames = [...filteredData]
      .sort((a, b) => dayjs(a.playedAt).diff(b.playedAt, 'seconds'))

    if (grouping != null) {
      const grouped = playedGames.reduce((acc, game) => {
        const key = dayjs(game.playedAt).startOf(grouping).toISOString()
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(game)
        return acc
      }, {} as Record<string, PlayedGameData[]>)

      playedGames = Object.entries(grouped).map(([date, e], i) => ({
        id: i,
        playedAt: date,
        points: Math.round(e.reduce((acc, game) => acc + game.points, 0) / e.length),
        bombsCaught: Math.round(e.reduce((acc, game) => acc + game.bombsCaught, 0) / e.length),
        mushroomsCaught: Math.round(e.reduce((acc, game) => acc + game.mushroomsCaught, 0) / e.length),
        paperBoatCaught: Math.round(e.reduce((acc, game) => acc + game.paperBoatCaught, 0) / e.length),
        foodInfo: [],
        taskToRemember: [],
        answer: [],
        averageFoodLifetimeMilliseconds: (e.reduce((acc, game) => acc + game.averageFoodLifetimeMilliseconds, 0) / e.length),
        percentOfCaughtFood: Math.round(e.reduce((acc, game) => acc + game.percentOfCaughtFood, 0) / e.length),
        percentOfDestroyedFood: Math.round(e.reduce((acc, game) => acc + game.percentOfDestroyedFood, 0) / e.length),
        percentOfMissPlacedFood: Math.round(e.reduce((acc, game) => acc + game.percentOfMissPlacedFood, 0) / e.length),
        countedRight: true,
        gameLengthSeconds: 0,
      }))
        .sort((a, b) => dayjs(a.playedAt).diff(b.playedAt, 'seconds'))
    }

    const successRateTh = 85
    const successRateData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: '% Caught',
          data: playedGames.map(e => e.percentOfCaughtFood),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y < successRateTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y < successRateTh ? thReachedColor : thTopColor,
        },
        {
          label: '% Misplaced',
          data: playedGames.map(e => e.percentOfMissPlacedFood),
          borderColor: '#6b7bbc',
          backgroundColor: 'rgba(0,21,158,0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: '#6b7bbc',
          pointBorderColor: '#6b7bbc',
        },
        {
          label: '% Destroyed',
          data: playedGames.map(e => e.percentOfDestroyedFood),
          borderColor: '#bc6b6b',
          backgroundColor: 'rgba(158,0,0,0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: '#bc6b6b',
          pointBorderColor: '#bc6b6b',
        },
      ]
    }
    const pointsTh = 30
    const pointsData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.points.title'),
          data: playedGames.map(e => e.points),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y < pointsTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y < pointsTh ? thReachedColor : thTopColor,
        },
      ]
    }
    const foodLifetimeData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: "Average Food Lifetime (ms)",
          data: playedGames.map(e => e.averageFoodLifetimeMilliseconds * 1000),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: thTopColor,
          pointBorderColor: thTopColor,
        },
      ]
    }
    const goodBadProjectilesData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: "Bombs",
          data: playedGames.map(e => e.bombsCaught),
          borderColor: '#bc6b6b',
          backgroundColor: 'rgba(158,0,0,0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: "Mushrooms",
          data: playedGames.map(e => e.mushroomsCaught),
          borderColor: '#a56bbc',
          backgroundColor: 'rgba(87,0,158,0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: "Paper boat",
          data: playedGames.map(e => e.paperBoatCaught),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }

    const successRateChartCtx = document.getElementById(`success-rate-chart-fruit-reaction`)
    if (successRateChartCtx) {
      // @ts-ignore
      charts.push(new Chart(successRateChartCtx, {
        ...lineChartConfigWithThreshold(successRateTh, `${successRateTh}%`),
        data: successRateData
      }))
    }
    const pointsChartCtx = document.getElementById(`points-chart-fruit-reaction`)
    if (pointsChartCtx) {
      // @ts-ignore
      charts.push(new Chart(pointsChartCtx, {
        ...lineChartConfigWithThreshold(pointsTh, `${pointsTh}`),
        data: pointsData
      }))
    }
    const foodLifetimeChartCtx = document.getElementById(`food-lifetime-chart-fruit-reaction`)
    if (foodLifetimeChartCtx) {
      // @ts-ignore
      charts.push(new Chart(foodLifetimeChartCtx, {
        ...lineChartConfig(),
        data: foodLifetimeData
      }))
    }
    const goodBadChartCtx = document.getElementById(`good-bad-projectiles-chart-fruit-reaction`)
    if (goodBadChartCtx) {
      // @ts-ignore
      charts.push(new Chart(goodBadChartCtx, {
        ...lineChartConfig(),
        data: goodBadProjectilesData
      }))
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [filteredData, grouping])

  useEffect(() => {
    const charts: Chart[] = []
    const caughtRight = ['#8fbc6b', '#72ab42']
    const caughtWrong = ['#6b82bc', '#4257ab']
    const notCaught = ['#bc6b6b', '#ab4242']
    for (const id of expandedIds) {
      const gameData = fullData[id - 1].foodInfo ?? []
      const chartData = {
        labels: gameData.map(() => ``),
        datasets: [
          {
            label: 'Right caught food lifetime',
            data: gameData.map(e => e.wasCaught && e.wasCaughtRight ? e.lifeTime : null),
            barPercentage: 1.5,
            borderColor: caughtRight[0],
            hoverBorderColor: caughtRight[0],
            backgroundColor: caughtRight[0],
            hoverBackgroundColor: caughtRight[1],
            borderWidth: 2,
            borderRadius: {
              topLeft: 5,
              topRight: 5
            },
          },
          {
            label: 'Wrong caught food lifetime',
            data: gameData.map(e => e.wasCaught && !e.wasCaughtRight ? e.lifeTime : null),
            barPercentage: 1.5,
            borderColor: caughtWrong[0],
            hoverBorderColor: caughtWrong[0],
            backgroundColor: caughtWrong[0],
            hoverBackgroundColor: caughtWrong[1],
            borderWidth: 2,
            borderRadius: {
              topLeft: 5,
              topRight: 5
            },
          },
          {
            label: 'Not caught food lifetime',
            data: gameData.map(e => !e.wasCaught ? e.lifeTime : null),
            barPercentage: 1.5,
            borderColor: notCaught[0],
            hoverBorderColor: notCaught[0],
            backgroundColor: notCaught[0],
            hoverBackgroundColor: notCaught[1],
            borderWidth: 2,
            borderRadius: {
              topLeft: 5,
              topRight: 5
            },
          },
        ]
      }

      const ctx = document.getElementById(`food-lifetime-chart-${id}-fruit-reaction`)
      const config = barChartConfig

      // @ts-ignore
      const chart = new Chart(ctx, {...config, data: chartData})
      charts.push(chart)
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [expandedIds])

  const handleExpand = (item: PlayedGameData) => {
    if (expandedIds.includes(item.id)) {
      setExpandedIds(expandedIds.filter((id) => id !== item.id))
    } else {
      setExpandedIds(expandedIds.concat(item.id))
    }
  }

  const rowProps = {
    onClick: handleExpand,
  }
  const rowOptions = {
    renderAfterRow: (item: PlayedGameData) => (
      <>
        {expandedIds.includes(item.id) && <tr style={{display: "flex", gridColumn: "1 / -1", position: 'relative'}}>
            <canvas id={`food-lifetime-chart-${item.id}-fruit-reaction`}></canvas>
        </tr>}
      </>
    ),
  }

  const tableColumns = [
    {label: '#', renderCell: (item) => fullData.length - item.id + 1},
    {
      label: translateLocalazy('patient.game-stats.chart.points.title'), renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        return numberComparison(prevItem?.points, item.points)
      }
    },
    {
      label: 'Counted right', renderCell: (item) => {
        return item.countedRight ? 'Yes' : 'No'
      }
    },
    {
      label: '% of caught food', renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        return numberComparison(prevItem?.percentOfCaughtFood, item.percentOfCaughtFood)
      }
    },
    {
      label: 'Average food lifetime', renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        return numberComparison(prevItem?.averageFoodLifetimeMilliseconds, item.averageFoodLifetimeMilliseconds, 's', true)
      }
    },
    {
      label: translateLocalazy('patient.game-stats.played-at'),
      renderCell: (item) => formatDate(item.playedAt),
    },
  ]

  const pagination = usePagination({nodes: filteredData}, {
    state: {
      page: 0,
      size: 10,
    },
  })

  return <>
    {filteredData.length == 0 &&
        <div className="card-box no-data flex flex-column">
            <img src={noDataImg}/>
            <div className="no-data-text">{fullData.length === 0 ?
              translateLocalazy('patient.game-stats.no-data') :
              translateLocalazy('patient.game-stats.no-data-for-period')}</div>
        </div>
    }

    {filteredData.length > 0 && <div className="card-box">
        <div>
            <CompactTable
                columns={tableColumns}
                data={{nodes: filteredData}}
                theme={theme}
                rowProps={rowProps}
                rowOptions={rowOptions}
                pagination={pagination}
            />
        </div>
        <br/>
        <div className="pagination">
            <span>Total Pages: {pagination.state.getTotalPages(filteredData)}</span>

            <span>
          {translateLocalazy('patient.game-stats.page')}&nbsp;

              {pagination.state.getPages(filteredData).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={classNames({
                    'active': pagination.state.page === i
                  })}
                  onClick={() => pagination.fns.onSetPage(i)}
                >
                  {i + 1}
                </button>
              ))}
        </span>
        </div>
    </div>}
    <div className="flex justify-end margin-me--btm">
      <SelectPicker
        name="group-by"
        menuAutoWidth={true}
        onChange={setGrouping}
        data={[
          {label: translateLocalazy('patient.game-stats.group-by-day'), value: GroupingType.day},
          {label: translateLocalazy('patient.game-stats.group-by-week'), value: GroupingType.week},
          {
            label: translateLocalazy('patient.game-stats.group-by-month'),
            value: GroupingType.month
          }]}
        placeholder={translateLocalazy('patient.game-stats.group-by-placeholder')}
      />
    </div>
    <div className="card-box">
      <canvas id="success-rate-chart-fruit-reaction"></canvas>
    </div>
    <div className="card-box">
      <canvas id="points-chart-fruit-reaction"></canvas>
    </div>
    <div className="card-box">
      <canvas id="food-lifetime-chart-fruit-reaction"></canvas>
    </div>
    <div className="card-box">
      <canvas id="good-bad-projectiles-chart-fruit-reaction"></canvas>
    </div>
  </>
}
